import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Loader2, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, pushPermission, enablePush } from "@/lib/push";

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unread: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const seenRef = useRef(null);
  const [pushState, setPushState] = useState(pushPermission());

  const turnOnPush = async () => {
    try { await enablePush(); setPushState("granted"); toast.success("Background alerts on — we'll notify you even when Pula is closed."); }
    catch (e) { toast.error(e.message || "Could not enable alerts."); setPushState(pushPermission()); }
  };

  const load = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setData(data);
      const items = data.items || [];
      if (seenRef.current === null) {
        seenRef.current = new Set(items.map((i) => i.notif_id));
      } else {
        const fresh = items.filter((i) => !i.read && !seenRef.current.has(i.notif_id));
        fresh.slice(0, 3).forEach((n) => {
          toast(n.text, { icon: "🔔", duration: 6000, action: n.link ? { label: "View", onClick: () => navigate(n.link) } : undefined });
        });
        items.forEach((i) => seenRef.current.add(i.notif_id));
      }
    } catch {}
  };
  useEffect(() => {
    if (user) { seenRef.current = null; load(); const t = setInterval(load, 30000); return () => clearInterval(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  if (!user) return null;

  const openPanel = async () => {
    setOpen((o) => !o);
    if (!open && data.unread > 0) {
      setLoading(true);
      try { await api.post("/notifications/read"); setData((d) => ({ ...d, unread: 0 })); } catch {}
      finally { setLoading(false); }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={openPanel} className="relative p-2 rounded-lg hover:bg-pula-bg2 transition-colors">
        <Bell className="w-5 h-5 text-pula-ink" />
        {data.unread > 0 && <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-pula-accent text-white text-[10px] grid place-items-center font-bold">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 w-80 pula-card p-2 shadow-xl max-h-96 overflow-y-auto" data-testid="notif-panel">
          <p className="font-heading font-bold text-pula-ink px-2 py-1 text-sm">Notifications</p>
          {pushSupported() && pushState !== "granted" && pushState !== "denied" && (
            <button data-testid="enable-push" onClick={turnOnPush}
              className="w-full flex items-center gap-2 px-2 py-2 mb-1 rounded-lg text-sm font-body text-pula-green bg-pula-green/5 hover:bg-pula-green/10 transition-colors">
              <BellRing className="w-4 h-4" /> Turn on background alerts
            </button>
          )}
          {loading && <Loader2 className="w-4 h-4 animate-spin text-pula-green mx-auto my-3" />}
          {data.items.length === 0 ? <p className="text-sm text-pula-inksoft font-body px-2 py-4">Nothing yet.</p> : (
            <div className="space-y-1">
              {data.items.map((n) => {
                const urgent = typeof n.text === "string" && n.text.startsWith("🚨");
                return (
                <button key={n.notif_id} data-testid={`notif-${n.notif_id}`} onClick={() => { setOpen(false); if (n.link) navigate(n.link); }}
                  className={`w-full text-left px-2 py-2 rounded-lg text-sm font-body hover:bg-pula-bg2 ${urgent ? "bg-pula-accent/10 border-l-2 border-pula-accent" : ""} ${n.read && !urgent ? "text-pula-inksoft" : "text-pula-ink font-medium"}`}>
                  {n.text}
                  <span className="block text-[11px] text-pula-inksoft mt-0.5">{new Date(n.created_at).toLocaleDateString()}</span>
                </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
